import { Injectable } from "@angular/core"
import { HttpParams } from "@angular/common/http"
import { Observable, forkJoin, map } from "rxjs"
import { environment } from "../../environments/environment"
import { ApiService } from "./api.service"

export interface UserAnalyticsSummary {
  total_signups: number
  verified_count: number
  pending_verification_count: number
  verification_rate: number
  inactive_count: number
  ordered_count: number
  not_ordered_count: number
  individual_count: number
  business_count: number
}

export interface UserAnalyticsTrendPoint {
  label: string
  signups: number
}

export interface AdvertiserAnalyticsUser {
  user_id: number
  firstname: string
  lastname: string
  email: string
  signup_date: string | null
  account_type: string
  business_name: string | null
  signup_purpose: string | null
  verification_status: "verified" | "pending"
  order_status: "ordered" | "not_ordered"
  last_login: string | null
  is_inactive: boolean
  inactivity_reminder_count: number
}

export interface AdvertiserAnalyticsDetail {
  user_id: number
  email: string
  firstname: string
  lastname: string
  phonenumber: string | null
  account_type: string
  business_name: string | null
  signup_purpose: string | null
  signup_date: string | null
  verification_status: "verified" | "pending"
  email_verified_at: string | null
  days_to_verify: number | null
  is_active: boolean
  last_login: string | null
  sms_notifications_enabled: boolean
  inactivity_reminder_count: number
  last_inactivity_reminder_sent_at: string | null
  recordings_count: number
  commercial_requests_count: number
  order_count: number
  total_order_value: number
  order_status: "ordered" | "not_ordered"
  first_order_at: string | null
  days_to_first_order: number | null
  stations_ordered_count: number
}

export interface UserAnalyticsPagination {
  total: number
  limit: number
  offset: number
  has_more: boolean
}

export interface UserAnalyticsResponse {
  summary: UserAnalyticsSummary
  trend: UserAnalyticsTrendPoint[]
  users: AdvertiserAnalyticsUser[]
  pagination: UserAnalyticsPagination
}

export interface UserAnalyticsDateFilters {
  date_from?: string
  date_to?: string
  granularity?: "day" | "week"
}

export interface UserAnalyticsUserFilters extends UserAnalyticsDateFilters {
  account_type?: string
  activity?: string
  verification?: string
  search?: string
  limit?: number
  offset?: number
}

export interface UserAnalyticsFilters extends UserAnalyticsUserFilters {
  activity_status?: string
  verification_status?: string
}

/** GET /admin/user-analytics/users/{id} → activity_funnel.pages[] */
export interface ActivityFunnelPage {
  page_path: string
  screen_page_views: number
}

export interface ActivityFunnel {
  source: string
  available: boolean
  start_date: string | null
  end_date: string | null
  lookback_days: number | null
  reason: string | null
  pages: ActivityFunnelPage[]
}

/** GET /admin/user-analytics/app-activity → ga4.active_users_by_country[] */
export interface AppActivityCountry {
  country: string
  active_users: number
}

/** ga4.active_users_by_city[] */
export interface AppActivityCity {
  city: string
  active_users: number
}

/** ga4.top_events[] */
export interface AppActivityEvent {
  event_name: string
  event_count: number
}

/** ga4.active_users_trend[] */
export interface AppActivityTrendPoint {
  date: string
  active_users: number
}

export interface AppActivity {
  available: boolean
  reason: string | null
  date_from: string | null
  date_to: string | null
  active_users_by_country: AppActivityCountry[]
  active_users_by_city: AppActivityCity[]
  top_events: AppActivityEvent[]
  active_users_trend: AppActivityTrendPoint[]
}

@Injectable({ providedIn: "root" })
export class UserAnalyticsService {
  private readonly summaryUrl = `${environment.apiUrl}/admin/user-analytics/summary`
  private readonly usersUrl = `${environment.apiUrl}/admin/user-analytics/users`
  private readonly appActivityUrl = `${environment.apiUrl}/admin/user-analytics/app-activity`

  constructor(private api: ApiService) {}

  getSummary(filters: UserAnalyticsDateFilters = {}): Observable<{
    summary: UserAnalyticsSummary
    trend: UserAnalyticsTrendPoint[]
  }> {
    return this.api
      .authenticatedGet<Record<string, unknown>>(this.summaryUrl, {
        params: this.toParams({
          date_from: filters.date_from,
          date_to: filters.date_to,
          granularity: filters.granularity,
        }),
      })
      .pipe(map((res) => this.normalizeSummary(res)))
  }

  getUsers(filters: UserAnalyticsUserFilters = {}): Observable<{
    users: AdvertiserAnalyticsUser[]
    pagination: UserAnalyticsPagination
  }> {
    return this.api
      .authenticatedGet<Record<string, unknown>>(this.usersUrl, {
        params: this.toParams({
          date_from: filters.date_from,
          date_to: filters.date_to,
          account_type: filters.account_type,
          activity: filters.activity,
          verification: filters.verification,
          search: filters.search,
          limit: filters.limit,
          offset: filters.offset,
        }),
      })
      .pipe(map((res) => this.normalizeUsers(res, filters)))
  }

  getUser(userId: number): Observable<AdvertiserAnalyticsDetail> {
    return this.api
      .authenticatedGet<Record<string, unknown>>(`${this.usersUrl}/${userId}`)
      .pipe(map((res) => this.parseDetail(res)))
  }

  /**
   * One request when the advertiser dialog opens. The database user and the
   * GA4 funnel are siblings on this response; the users list does not include
   * activity_funnel.
   */
  getAdvertiserDetail(userId: number): Observable<{
    user: AdvertiserAnalyticsDetail
    activityFunnel: ActivityFunnel
  }> {
    return this.api
      .authenticatedGet<Record<string, unknown>>(`${this.usersUrl}/${userId}`)
      .pipe(
        map((res) => ({
          user: this.parseDetail(res),
          activityFunnel: this.parseActivityFunnel(res),
        })),
      )
  }

  /** Same date_from / date_to query the summary endpoint already uses. */
  getAppActivity(filters: UserAnalyticsDateFilters): Observable<AppActivity> {
    return this.api
      .authenticatedGet<Record<string, unknown>>(this.appActivityUrl, {
        params: this.toParams({
          date_from: filters.date_from,
          date_to: filters.date_to,
        }),
      })
      .pipe(map((res) => this.parseAppActivity(res)))
  }

  getAnalytics(filters: UserAnalyticsFilters = {}): Observable<UserAnalyticsResponse> {
    const dateFilters: UserAnalyticsDateFilters = {
      date_from: filters.date_from,
      date_to: filters.date_to,
      granularity: filters.granularity,
    }
    const userFilters: UserAnalyticsUserFilters = {
      ...dateFilters,
      account_type: filters.account_type,
      activity: filters.activity || filters.activity_status,
      verification: filters.verification || filters.verification_status,
      search: filters.search,
      limit: filters.limit,
      offset: filters.offset,
    }
    return forkJoin({
      summary: this.getSummary(dateFilters),
      users: this.getUsers(userFilters),
    }).pipe(
      map(({ summary, users }) => ({
        summary: summary.summary,
        trend: summary.trend,
        users: users.users,
        pagination: users.pagination,
      })),
    )
  }

  private toParams(filters: Record<string, unknown>): HttpParams {
    let params = new HttpParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params = params.set(key, String(value))
      }
    })
    return params
  }

  private normalizeSummary(res: Record<string, unknown>): {
    summary: UserAnalyticsSummary
    trend: UserAnalyticsTrendPoint[]
  } {
    const body = (res?.["data"] as Record<string, unknown>) || res || {}
    const breakdown = (body["account_type_breakdown"] as Record<string, unknown>) || {}
    const total = this.num(body["total_signups"] ?? body["total"] ?? body["total_users"])
    const verified = this.num(body["verified_count"] ?? body["verified"])
    const pending = this.num(
      body["pending_count"] ?? body["pending_verification_count"] ?? body["unverified_count"],
    )
    const rateRaw = body["verification_completion_rate"] ?? body["verification_rate"]
    const verificationRate =
      rateRaw != null && rateRaw !== ""
        ? Number(rateRaw)
        : total > 0
          ? (verified / total) * 100
          : 0

    return {
      summary: {
        total_signups: total,
        verified_count: verified,
        pending_verification_count: pending,
        verification_rate: Number.isFinite(verificationRate) ? verificationRate : 0,
        inactive_count: this.num(body["inactive_count"]),
        ordered_count: this.num(body["ordered_count"] ?? body["ordered"]),
        not_ordered_count: this.num(body["not_ordered_count"] ?? body["not_ordered"]),
        individual_count: this.num(breakdown["individual"] ?? body["individual_count"]),
        business_count: this.num(breakdown["business"] ?? body["business_count"]),
      },
      trend: this.normalizeTrend(body["trend"]),
    }
  }

  private normalizeUsers(
    res: Record<string, unknown>,
    filters: UserAnalyticsUserFilters,
  ): { users: AdvertiserAnalyticsUser[]; pagination: UserAnalyticsPagination } {
    const body = (res?.["data"] as Record<string, unknown>) || res || {}
    const usersRaw = body["users"] ?? body["records"] ?? body["advertisers"] ?? []
    const users = Array.isArray(usersRaw)
      ? usersRaw.map((row) => this.normalizeUser(row as Record<string, unknown>))
      : []

    const paginationRaw = (body["pagination"] as Record<string, unknown>) || {}
    const limit = this.num(paginationRaw["limit"] ?? filters.limit, 10)
    const offset = this.num(paginationRaw["offset"] ?? filters.offset, 0)
    const totalRows = this.num(paginationRaw["total"] ?? body["total"], users.length)

    return {
      users,
      pagination: {
        total: totalRows,
        limit,
        offset,
        has_more: Boolean(paginationRaw["has_more"] ?? offset + users.length < totalRows),
      },
    }
  }

  private normalizeTrend(raw: unknown): UserAnalyticsTrendPoint[] {
    if (!raw) return []
    if (Array.isArray(raw)) {
      return raw.map((item) => {
        if (item && typeof item === "object") {
          const row = item as Record<string, unknown>
          return {
            label: String(row["label"] ?? row["date"] ?? row["day"] ?? ""),
            signups: this.num(row["signups"] ?? row["count"] ?? row["value"]),
          }
        }
        return { label: "", signups: 0 }
      })
    }
    if (typeof raw === "object") {
      const obj = raw as Record<string, unknown>
      const labels = Array.isArray(obj["labels"]) ? (obj["labels"] as unknown[]) : []
      const signups = Array.isArray(obj["signups"] ?? obj["counts"])
        ? ((obj["signups"] ?? obj["counts"]) as unknown[])
        : []
      return labels.map((label, i) => ({
        label: String(label ?? ""),
        signups: this.num(signups[i]),
      }))
    }
    return []
  }

  private normalizeUser(row: Record<string, unknown>): AdvertiserAnalyticsUser {
    return {
      user_id: this.num(row["user_id"]),
      firstname: String(row["firstname"] ?? ""),
      lastname: String(row["lastname"] ?? ""),
      email: String(row["email"] ?? ""),
      signup_date: this.nullableString(row["signup_date"]),
      account_type: String(row["account_type"] ?? ""),
      business_name: this.nullableString(row["business_name"]),
      signup_purpose: this.nullableString(row["signup_purpose"]),
      verification_status: row["verification_status"] === "verified" ? "verified" : "pending",
      order_status: row["order_status"] === "ordered" ? "ordered" : "not_ordered",
      last_login: this.nullableString(row["last_login"]),
      is_inactive: this.isTrue(row["is_inactive"]),
      inactivity_reminder_count: this.num(row["inactivity_reminder_count"]),
    }
  }

  private parseDetail(res: Record<string, unknown>): AdvertiserAnalyticsDetail {
    const user = (res?.["user"] && typeof res["user"] === "object"
      ? res["user"]
      : {}) as Record<string, unknown>
    return {
      user_id: this.num(user["user_id"]),
      email: String(user["email"] ?? ""),
      firstname: String(user["firstname"] ?? ""),
      lastname: String(user["lastname"] ?? ""),
      phonenumber: this.nullableString(user["phonenumber"]),
      account_type: String(user["account_type"] ?? ""),
      business_name: this.nullableString(user["business_name"]),
      signup_purpose: this.nullableString(user["signup_purpose"]),
      signup_date: this.nullableString(user["signup_date"]),
      verification_status: user["verification_status"] === "verified" ? "verified" : "pending",
      email_verified_at: this.nullableString(user["email_verified_at"]),
      days_to_verify: this.nullableNum(user["days_to_verify"]),
      is_active: user["is_active"] === true,
      last_login: this.nullableString(user["last_login"]),
      sms_notifications_enabled: user["sms_notifications_enabled"] === true,
      inactivity_reminder_count: this.num(user["inactivity_reminder_count"]),
      last_inactivity_reminder_sent_at: this.nullableString(user["last_inactivity_reminder_sent_at"]),
      recordings_count: this.num(user["recordings_count"]),
      commercial_requests_count: this.num(user["commercial_requests_count"]),
      order_count: this.num(user["order_count"]),
      total_order_value: this.num(user["total_order_value"]),
      order_status: user["order_status"] === "ordered" ? "ordered" : "not_ordered",
      first_order_at: this.nullableString(user["first_order_at"]),
      days_to_first_order: this.nullableNum(user["days_to_first_order"]),
      stations_ordered_count: this.num(user["stations_ordered_count"]),
    }
  }

  private parseActivityFunnel(res: Record<string, unknown>): ActivityFunnel {
    const raw = res?.["activity_funnel"]
    if (!raw || typeof raw !== "object") {
      return this.emptyFunnel()
    }
    const funnel = raw as Record<string, unknown>
    const pagesRaw = funnel["pages"]
    const pages = Array.isArray(pagesRaw)
      ? pagesRaw
          .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
          .map((row) => ({
            page_path: String(row["page_path"] ?? ""),
            screen_page_views: this.num(row["screen_page_views"]),
          }))
      : []
    return {
      source: String(funnel["source"] ?? ""),
      available: funnel["available"] === true,
      start_date: this.nullableString(funnel["start_date"]),
      end_date: this.nullableString(funnel["end_date"]),
      lookback_days: this.nullableNum(funnel["lookback_days"]),
      reason: this.nullableString(funnel["reason"]),
      pages,
    }
  }

  private emptyFunnel(): ActivityFunnel {
    return {
      source: "",
      available: false,
      start_date: null,
      end_date: null,
      lookback_days: null,
      reason: null,
      pages: [],
    }
  }

  private parseAppActivity(res: Record<string, unknown>): AppActivity {
    const ga4Raw = res?.["ga4"]
    const ga4 = ga4Raw && typeof ga4Raw === "object" ? (ga4Raw as Record<string, unknown>) : {}
    return {
      available: ga4["available"] === true,
      reason: this.nullableString(ga4["reason"]),
      date_from: this.nullableString(res?.["date_from"]),
      date_to: this.nullableString(res?.["date_to"]),
      active_users_by_country: this.mapRows(ga4["active_users_by_country"], (row) => ({
        country: String(row["country"] ?? ""),
        active_users: this.num(row["active_users"]),
      })),
      active_users_by_city: this.mapRows(ga4["active_users_by_city"], (row) => ({
        city: String(row["city"] ?? ""),
        active_users: this.num(row["active_users"]),
      })),
      top_events: this.mapRows(ga4["top_events"], (row) => ({
        event_name: String(row["event_name"] ?? ""),
        event_count: this.num(row["event_count"]),
      })),
      active_users_trend: this.mapRows(ga4["active_users_trend"], (row) => ({
        date: String(row["date"] ?? ""),
        active_users: this.num(row["active_users"]),
      })),
    }
  }

  private mapRows<T>(raw: unknown, mapRow: (row: Record<string, unknown>) => T): T[] {
    if (!Array.isArray(raw)) return []
    return raw
      .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
      .map(mapRow)
  }

  private isTrue(value: unknown): boolean {
    return value === true || value === 1 || value === "true" || value === "1"
  }

  private nullableString(value: unknown): string | null {
    if (value == null || value === "") return null
    return String(value)
  }

  private nullableNum(value: unknown): number | null {
    if (value == null || value === "") return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }

  private num(value: unknown, fallback = 0): number {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
}
